// components/Teams/interview/EndInterviewModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../../components/Common/Toast';
import config from '../../../config';
import {
  FaTimes, FaUserCheck, FaUserTimes, FaUserClock,
  FaChartBar, FaCheck, FaClock
} from 'react-icons/fa';
import './EndInterviewModal.css';

const EndInterviewModal = ({
  show,
  onClose,
  interviewId,
  teamId,
  token,
  onAllReviewed,
  onCandidateAction
}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (show && interviewId) {
      fetchCandidates();
    }
  }, [show, interviewId]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/interview/${interviewId}/candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (data.success) {
        // Don't initialize status - only show what's in the database
        // Status will be null/undefined until user makes a decision
        const candidatesWithStatus = (data.candidates || []).map(c => ({
          ...c,
          status: c.status || null // null means unreviewed, not 'pending'
        }));
        setCandidates(candidatesWithStatus);
      } else {
        toast.error(data.message || 'Failed to fetch candidates');
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (candidateId, action) => {
    // Prevent double-clicks
    if (actionLoading[candidateId]) return;
    
    setActionLoading(prev => ({ ...prev, [candidateId]: true }));
    
    try {
      const addToTeam = action === 'selected';
      const { data } = await axios.post(
        `${config.baseURL}/api/teams/interview/${interviewId}/end`,
        {
          candidateId,
          decision: action,
          add_to_team: addToTeam,
          team_id: teamId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        const messages = {
          selected: data.team_member_added ? 'Candidate selected and added to team!' : 'Candidate selected',
          rejected: 'Candidate rejected',
          pending: 'Candidate marked as pending'
        };
        toast.success(messages[action] || 'Status updated');
        
        // Update local state
        setCandidates(prev => prev.map(c => 
          c.candidate_id === candidateId ? { ...c, status: action } : c
        ));

        // Call parent callback if provided
        if (onCandidateAction) {
          onCandidateAction(candidateId, action);
        }

        // If all candidates have been decided (not null/pending), notify parent
        const updatedCandidates = candidates.map(c => 
          c.candidate_id === candidateId ? { ...c, status: action } : c
        );
        const allDecided = updatedCandidates.every(c => 
          c.status === 'selected' || c.status === 'rejected'
        );
        
        if (allDecided && onAllReviewed) {
          onAllReviewed();
        }
      }
    } catch (error) {
      console.error('Error updating candidate:', error);
      toast.error('Failed to update candidate status');
    } finally {
      setActionLoading(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  const handleViewAnalytics = (candidateId) => {
    // Open analytics modal
    window.dispatchEvent(new CustomEvent('openAnalyticsModal', {
      detail: { 
        interviewId, 
        candidateId 
      }
    }));
  };

  const reviewedCount = candidates.filter(c => 
    c.status === 'selected' || c.status === 'rejected'
  ).length;
  
  const pendingCount = candidates.filter(c => 
    c.status === 'pending' || c.status === null
  ).length;
  
  const allDecided = reviewedCount === candidates.length && candidates.length > 0;

  if (!show) return null;

  return (
    <div className="end-interview-overlay" onClick={onClose}>
      <div className="end-interview-modal" onClick={e => e.stopPropagation()}>
        <div className="end-interview-header">
          <div>
            <h2>Review Candidates</h2>
            <p className="subtitle">
              Manually accept, reject, or mark candidates as pending
            </p>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading candidates...</p>
          </div>
        ) : candidates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Candidates Found</h3>
            <p>No candidates have joined this interview yet</p>
          </div>
        ) : (
          <>
            <div className="review-progress">
              <div className="progress-header">
                <span>Review Progress</span>
                <span className="progress-count">
                  {reviewedCount} reviewed, {pendingCount} unreviewed
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${candidates.length > 0 ? (reviewedCount / candidates.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="candidates-list">
              {candidates.map(candidate => (
                <div 
                  key={candidate.candidate_id} 
                  className={`candidate-card ${candidate.status || 'unreviewed'}`}
                >
                  <div className="candidate-info">
                    <div className="candidate-name-section">
                      <h4>{candidate.first_name} {candidate.last_name}</h4>
                      {candidate.status === 'selected' && (
                        <span className="status-badge-mini selected">✓ Selected</span>
                      )}
                      {candidate.status === 'rejected' && (
                        <span className="status-badge-mini rejected">✗ Rejected</span>
                      )}
                      {candidate.status === 'pending' && (
                        <span className="status-badge-mini pending">⏳ Pending</span>
                      )}
                      {!candidate.status && (
                        <span className="status-badge-mini unreviewed">⬜ Unreviewed</span>
                      )}
                    </div>
                    <span className="candidate-email">{candidate.email}</span>
                    
                    <div className="answer-progress">
                      <div className="progress-label">
                        <span>Questions answered</span>
                        <span className="progress-count">
                          {candidate.answered_count}/{candidate.total_questions}
                        </span>
                      </div>
                      <div className="mini-progress">
                        <div 
                          className="mini-progress-fill"
                          style={{ 
                            width: `${candidate.completion_percentage || 0}%` 
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="completion-badge">
                      {candidate.completion_percentage || 0}% complete
                    </div>
                  </div>

                  <div className="candidate-actions">
                    <button
                      className={`action-btn select-btn ${candidate.status === 'selected' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.candidate_id, 'selected')}
                      disabled={actionLoading[candidate.candidate_id]}
                      title="Select Candidate"
                    >
                      <FaUserCheck />
                      <span>
                        {actionLoading[candidate.candidate_id] && candidate.status !== 'selected' 
                          ? 'Selecting...' 
                          : candidate.status === 'selected' ? 'Selected' : 'Select'}
                      </span>
                    </button>
                    
                    <button
                      className={`action-btn reject-btn ${candidate.status === 'rejected' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.candidate_id, 'rejected')}
                      disabled={actionLoading[candidate.candidate_id]}
                      title="Reject Candidate"
                    >
                      <FaUserTimes />
                      <span>
                        {actionLoading[candidate.candidate_id] && candidate.status !== 'rejected'
                          ? 'Rejecting...' 
                          : candidate.status === 'rejected' ? 'Rejected' : 'Reject'}
                      </span>
                    </button>
                    
                    <button
                      className={`action-btn pending-btn ${candidate.status === 'pending' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.candidate_id, 'pending')}
                      disabled={actionLoading[candidate.candidate_id]}
                      title="Mark as Pending"
                    >
                      <FaUserClock />
                      <span>
                        {actionLoading[candidate.candidate_id] && candidate.status !== 'pending'
                          ? 'Updating...' 
                          : candidate.status === 'pending' ? 'Pending' : 'Pending'}
                      </span>
                    </button>
                    
                    <button
                      className="action-btn analytics-btn"
                      onClick={() => handleViewAnalytics(candidate.candidate_id)}
                      title="View Detailed Analytics"
                    >
                      <FaChartBar />
                      <span>Details</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="modal-footer">
          <div className="footer-info">
            {allDecided ? (
              <span className="all-reviewed-message">
                ✅ All candidates reviewed - you can close this window
              </span>
            ) : pendingCount > 0 ? (
              <span className="pending-message">
                ⚠️ {pendingCount} candidate{pendingCount !== 1 ? 's' : ''} still unreviewed
              </span>
            ) : (
              <span className="pending-message">
                ⚠️ Please review all candidates
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button className="eim-cancel-btn text-light" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndInterviewModal;





// // components/Teams/interview/EndInterviewModal.jsx
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { toast } from '../../../components/Common/Toast';
// import config from '../../../config';
// import {
//   FaTimes, FaUserCheck, FaUserTimes, FaUserClock,
//   FaChartBar, FaCheck, FaClock
// } from 'react-icons/fa';
// import './EndInterviewModal.css';

// const EndInterviewModal = ({
//   show,
//   onClose,
//   interviewId,
//   teamId,
//   token,
//   onAllReviewed,
//   onCandidateAction
// }) => {
//   const [candidates, setCandidates] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [actionLoading, setActionLoading] = useState({});

//   useEffect(() => {
//     if (show && interviewId) {
//       fetchCandidates();
//     }
//   }, [show, interviewId]);

//   const fetchCandidates = async () => {
//     try {
//       setLoading(true);
//       const { data } = await axios.get(
//         `${config.baseURL}/api/teams/interview/${interviewId}/candidates`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       if (data.success) {
//         // Initialize status as 'pending' if not already set
//         const candidatesWithStatus = (data.candidates || []).map(c => ({
//           ...c,
//           status: c.status || 'pending'
//         }));
//         setCandidates(candidatesWithStatus);
//       } else {
//         toast.error(data.message || 'Failed to fetch candidates');
//       }
//     } catch (error) {
//       console.error('Error fetching candidates:', error);
//       toast.error('Failed to fetch candidates');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAction = async (candidateId, action) => {
//     // Prevent double-clicks
//     if (actionLoading[candidateId]) return;
    
//     setActionLoading(prev => ({ ...prev, [candidateId]: true }));
    
//     try {
//       const addToTeam = action === 'selected';
//       const { data } = await axios.post(
//         `${config.baseURL}/api/teams/interview/${interviewId}/end`,
//         {
//           candidateId,
//           decision: action,
//           add_to_team: addToTeam,
//           team_id: teamId
//         },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       if (data.success) {
//         const messages = {
//           selected: data.team_member_added ? 'Candidate selected and added to team!' : 'Candidate selected',
//           rejected: 'Candidate rejected',
//           pending: 'Candidate marked as pending'
//         };
//         toast.success(messages[action] || 'Status updated');
        
//         // Update local state
//         setCandidates(prev => prev.map(c => 
//           c.candidate_id === candidateId ? { ...c, status: action } : c
//         ));

//         // Call parent callback if provided
//         if (onCandidateAction) {
//           onCandidateAction(candidateId, action);
//         }

//         // If all candidates have been decided (not pending), notify parent
//         const updatedCandidates = candidates.map(c => 
//           c.candidate_id === candidateId ? { ...c, status: action } : c
//         );
//         const allDecided = updatedCandidates.every(c => 
//           c.status === 'selected' || c.status === 'rejected'
//         );
        
//         if (allDecided && onAllReviewed) {
//           onAllReviewed();
//         }
//       }
//     } catch (error) {
//       console.error('Error updating candidate:', error);
//       toast.error('Failed to update candidate status');
//     } finally {
//       setActionLoading(prev => ({ ...prev, [candidateId]: false }));
//     }
//   };

//   const handleViewAnalytics = (candidateId) => {
//     // Open analytics modal
//     window.dispatchEvent(new CustomEvent('openAnalyticsModal', {
//       detail: { 
//         interviewId, 
//         candidateId 
//       }
//     }));
//   };

//   const reviewedCount = candidates.filter(c => 
//     c.status === 'selected' || c.status === 'rejected'
//   ).length;
  
//   const pendingCount = candidates.filter(c => c.status === 'pending').length;
//   const allDecided = reviewedCount === candidates.length && candidates.length > 0;

//   if (!show) return null;

//   return (
//     <div className="end-interview-overlay" onClick={onClose}>
//       <div className="end-interview-modal" onClick={e => e.stopPropagation()}>
//         <div className="end-interview-header">
//           <div>
//             <h2>Review Candidates</h2>
//             <p className="subtitle">
//               Manually accept, reject, or mark candidates as pending
//             </p>
//           </div>
//         </div>

//         {loading ? (
//           <div className="loading-state">
//             <div className="loading-spinner" />
//             <p>Loading candidates...</p>
//           </div>
//         ) : candidates.length === 0 ? (
//           <div className="empty-state">
//             <div className="empty-icon">👥</div>
//             <h3>No Candidates Found</h3>
//             <p>No candidates have joined this interview yet</p>
//           </div>
//         ) : (
//           <>
//             <div className="review-progress">
//               <div className="progress-header">
//                 <span>Review Progress</span>
//                 <span className="progress-count">
//                   {reviewedCount} reviewed, {pendingCount} pending
//                 </span>
//               </div>
//               <div className="progress-bar">
//                 <div 
//                   className="progress-fill"
//                   style={{ width: `${candidates.length > 0 ? (reviewedCount / candidates.length) * 100 : 0}%` }}
//                 />
//               </div>
//             </div>

//             <div className="candidates-list">
//               {candidates.map(candidate => (
//                 <div 
//                   key={candidate.candidate_id} 
//                   className={`candidate-card ${candidate.status}`}
//                 >
//                   <div className="candidate-info">
//                     <div className="candidate-name-section">
//                       <h4>{candidate.first_name} {candidate.last_name}</h4>
//                       {candidate.status === 'selected' && (
//                         <span className="status-badge-mini selected">✓ Selected</span>
//                       )}
//                       {candidate.status === 'rejected' && (
//                         <span className="status-badge-mini rejected">✗ Rejected</span>
//                       )}
//                       {candidate.status === 'pending' && (
//                         <span className="status-badge-mini pending">⏳ Pending</span>
//                       )}
//                     </div>
//                     <span className="candidate-email">{candidate.email}</span>
                    
//                     <div className="answer-progress">
//                       <div className="progress-label">
//                         <span>Questions answered</span>
//                         <span className="progress-count">
//                           {candidate.answered_count}/{candidate.total_questions}
//                         </span>
//                       </div>
//                       <div className="mini-progress">
//                         <div 
//                           className="mini-progress-fill"
//                           style={{ 
//                             width: `${candidate.completion_percentage || 0}%` 
//                           }}
//                         />
//                       </div>
//                     </div>
                    
//                     <div className="completion-badge">
//                       {candidate.completion_percentage || 0}% complete
//                     </div>
//                   </div>

//                   <div className="candidate-actions">
//                     <button
//                       className={`action-btn select-btn ${candidate.status === 'selected' ? 'active' : ''}`}
//                       onClick={() => handleAction(candidate.candidate_id, 'selected')}
//                       disabled={actionLoading[candidate.candidate_id]}
//                       title="Select Candidate"
//                     >
//                       <FaUserCheck />
//                       <span>
//                         {actionLoading[candidate.candidate_id] && candidate.status !== 'selected' 
//                           ? 'Selecting...' 
//                           : candidate.status === 'selected' ? 'Selected' : 'Select'}
//                       </span>
//                     </button>
                    
//                     <button
//                       className={`action-btn reject-btn ${candidate.status === 'rejected' ? 'active' : ''}`}
//                       onClick={() => handleAction(candidate.candidate_id, 'rejected')}
//                       disabled={actionLoading[candidate.candidate_id]}
//                       title="Reject Candidate"
//                     >
//                       <FaUserTimes />
//                       <span>
//                         {actionLoading[candidate.candidate_id] && candidate.status !== 'rejected'
//                           ? 'Rejecting...' 
//                           : candidate.status === 'rejected' ? 'Rejected' : 'Reject'}
//                       </span>
//                     </button>
                    
//                     <button
//                       className={`action-btn pending-btn ${candidate.status === 'pending' ? 'active' : ''}`}
//                       onClick={() => handleAction(candidate.candidate_id, 'pending')}
//                       disabled={actionLoading[candidate.candidate_id]}
//                       title="Mark as Pending"
//                     >
//                       <FaUserClock />
//                       <span>
//                         {actionLoading[candidate.candidate_id] && candidate.status !== 'pending'
//                           ? 'Updating...' 
//                           : candidate.status === 'pending' ? 'Pending' : 'Pending'}
//                       </span>
//                     </button>
                    
//                     <button
//                       className="action-btn analytics-btn"
//                       onClick={() => handleViewAnalytics(candidate.candidate_id)}
//                       title="View Detailed Analytics"
//                     >
//                       <FaChartBar />
//                       <span>Details</span>
//                     </button>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </>
//         )}

//         <div className="modal-footer">
//           <div className="footer-info">
//             {allDecided ? (
//               <span className="all-reviewed-message">
//                 ✅ All candidates reviewed - you can close this window
//               </span>
//             ) : pendingCount > 0 ? (
//               <span className="pending-message">
//                 ⚠️ {pendingCount} candidate{pendingCount !== 1 ? 's' : ''} still pending review
//               </span>
//             ) : (
//               <span className="pending-message">
//                 ⚠️ Please review all candidates
//               </span>
//             )}
//           </div>
//           <div className="footer-actions">
//             <button className="eim-cancel-btn text-light" onClick={onClose}>
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EndInterviewModal;